/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import EventEmitter from 'eventemitter3';

import TransitionsEnum from './transitions-enum';
import TransitionParser from './transitions-parser';

/**
 * @import { ViewerController } from '../viewer/controller'
 * @import CommunicatorSettings from '../communicator/settings/communicator-settings'
 */

const defaultTransition = TransitionsEnum.FadeIn;

/**
 * Ovladač sloužící jako zaobalovač s animací pro jiný ViewerController.
 * @implements {ViewerController}
 */
export default class TransitionsViewerController {
  /**
   * @param {CommunicatorSettings | undefined} communicatorSettings
   * @param {HTMLElement} el
   * @param {ViewerController} viewerController
   */
  constructor(communicatorSettings, el, viewerController) {
    this.communicatorSettings = communicatorSettings;
    this.el = el;
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();

    /** @type {TransitionsEnum} */
    this.transitionName = defaultTransition;
    this.viewerController = viewerController;
  }

  /** @type {ViewerController['destroy']} */
  destroy() {
    return this.viewerController.destroy();
  }

  /** @type {ViewerController['enter']} */
  enter(offset) {
    return this.viewerController.enter(offset);
  }

  /** @type {ViewerController['leave']} */
  async leave() {
    if (this.el?.classList.contains(this.transitionName)) {
      this.el?.classList.remove(this.transitionName);
    }
    return await this.viewerController.leave();
  }

  /** @type {ViewerController['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {ViewerController['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na chybu.
   * @param {Error} error Chybový objekt.
   * @private
   * @returns {undefined} Nic.
   */
  onError(error) {
    this.emitter.emit('error', error);
  }

  /**
   * Reakce na načtení slajdu.
   * @private
   * @returns {undefined} Nic.
   */
  onLoaded() {
    this.emitter.emit('loaded');
  }

  /**
   * Reakce na přehrání slajdu.
   * @private
   * @returns {undefined} Nic.
   */
  onPlayed() {
    this.emitter.emit('played');
  }

  /**
   * Reakce na začátek přehrávání.
   * @private
   */
  onPlaying() {
    if (this.transitionName !== TransitionsEnum.Empty) {
      this.el?.classList.add(this.transitionName);
    }
    this.emitter.emit('playing');
  }

  /** @type {ViewerController['release']} */
  async release() {
    this.viewerController.off('error', this.onError.bind(this));
    this.viewerController.off('loaded', this.onLoaded.bind(this));
    this.viewerController.off('played', this.onPlayed.bind(this));
    this.viewerController.off('playing', this.onPlaying.bind(this));
    await this.viewerController.release();
  }

  /** @type {ViewerController['seize']} */
  seize(scheduleInfo) {
    this.viewerController.on('error', this.onError.bind(this));
    this.viewerController.on('loaded', this.onLoaded.bind(this));
    this.viewerController.on('played', this.onPlayed.bind(this));
    this.viewerController.on('playing', this.onPlaying.bind(this));

    const transitionName = this.communicatorSettings?.get(
      'player.light.viewer.transition'
    );
    if (transitionName !== null) {
      this.transitionName =
        TransitionParser.parse(transitionName) ?? defaultTransition;
    }

    return this.viewerController.seize(scheduleInfo);
  }
}
